import { Calendar, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/hooks/use-language";
import { newsArticles } from "@/data/newsArticles";

const categoryBadge: Record<string, string> = {
  TGA: "bg-secondary/10 text-secondary",
  TLE: "bg-teal/10 text-teal",
  "IFRS 16": "bg-blue-500/10 text-blue-600",
  Tiego: "bg-accent/10 text-accent-foreground",
};

export default function LatestNewsTeaser() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { t, localePath, language } = useLanguage();
  const availableArticles = language === "en" 
    ? newsArticles.filter(a => a.titleEn) 
    : newsArticles;
  const latest = availableArticles[0];

  if (!latest) return null;

  return (
    <div ref={ref} className={`max-w-5xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="flex items-end justify-between gap-6 mb-6">
        <div>
          <span className="inline-block text-gold text-sm font-bold uppercase tracking-widest mb-3">
            {t("Senaste nytt", "Latest news")}
          </span>
          <h3 className="text-2xl md:text-3xl font-display text-foreground">
            {t("Läs vår senaste nyhet", "Read our latest news")}
          </h3>
        </div>
        <Button asChild variant="outline" className="hidden sm:inline-flex">
          <Link to={localePath("/nyheter")}>
            {t("Alla nyheter", "All news")}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>

      <Link
        to={localePath("/nyheter")}
        className="group block rounded-3xl border border-border/50 bg-card shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden"
        aria-label={`${t("Öppna nyheter", "Open news")}: ${latest.title}`}
      >
        <div className="p-8 lg:p-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {latest.categories.map((cat) => (
              <span key={cat} className={`px-3 py-1 text-xs font-medium rounded-full ${categoryBadge[cat] || "bg-muted text-muted-foreground"}`}>{cat}</span>
            ))}
            <span className="text-muted-foreground text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {latest.date}
            </span>
          </div>
          <h4 className="text-xl md:text-2xl font-display text-foreground mb-3 group-hover:text-primary transition-colors">{t(latest.title, latest.titleEn || latest.title)}</h4>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">{t(latest.excerpt, latest.excerptEn || latest.excerpt)}</p>
          <div className="mt-6 inline-flex items-center gap-2 text-primary font-medium">
            <span>{t("Läs mer", "Read more")}</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>
      </Link>

      <div className="mt-6 sm:hidden">
        <Button asChild variant="outline" className="w-full">
          <Link to={localePath("/nyheter")}>
            {t("Alla nyheter", "All news")}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
